import React, { useMemo } from "react";
import styled from "styled-components";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";
import { FaCaretDown, FaCaretUp } from "react-icons/fa";
import { GrNext, GrPrevious } from "react-icons/gr";
import { MdFirstPage, MdLastPage } from "react-icons/md";
import { Loader, PaginationButton, SpinnerIcon } from "../Styles";

const TableWrapper = styled.div`
  /* margin: 20px; */
  padding: 20px;
  border: 1px solid ${({ theme }) => theme.branding.borderColor};
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  max-width: 100%;
  background-color: ${({ theme }) => theme.branding.color};
`;

const TableContainer = styled.div`
  display: block;
  /* Adjust the 200px to account for top menu bar and pagination height */
  /* max-height: calc(100vh - 400px); */

  overflow-y: auto;
  overflow-x: auto; /* Enable horizontal scrolling */
`;

const TableElement = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
`;

const TableHeader = styled.thead`
  background-color: ${({ theme }) => theme.colors.background};
  position: sticky;
  top: 0px; /* Adjust this value to be below the menu bar */
  z-index: 999; /* Ensure it's below the menu bar */
`;

const TableHeaderCell = styled.th`
  padding: 10px;
  text-align: left;
  cursor: pointer;
  &:hover {
    background-color: ${({ theme }) => theme.branding.bgColorHover};
  }
  /* width: ${(props) => props.width || "auto"}; */

  word-wrap: break-word; /* Allow wrapping of content */
  white-space: normal; /* Ensure content wraps */
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  &:nth-child(even) {
    background-color: ${({ theme }) => theme.colors.background};
  }
  /* &:hover {
    background-color: ${({ theme }) => theme.branding.borderColor};
  } */
  /* cursor: pointer; */
`;

const TableCell = styled.td`
  padding: 10px;
  /* width: ${(props) => props.width || "auto"}; */

  word-wrap: break-word; /* Allow wrapping of content */
  white-space: normal; /* Ensure content wraps */
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 20px 0;
`;

const Pagination = styled.div`
  display: flex;
  gap: 8px;
`;

const PaginationInfo = styled.span`
  margin: 0 10px;
`;

const FilterInput = styled.input`
  margin-bottom: 10px;
  padding: 8px;
  width: 100%;
  box-sizing: border-box;
  border: 1px solid ${({ theme }) => theme.branding.borderColor};
  border-radius: 4px;
`;

const NoDataMessage = styled.div`
  padding: 20px;
  text-align: center;
  font-size: 1.2rem;
`;

function DefaultColumnFilter({
  column: { filterValue, preFilteredRows, setFilter },
}) {
  const count = preFilteredRows.length;

  return (
    <FilterInput
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      placeholder={`Search ${count} records...`}
    />
  );
}

function Table({ loading, data, columns }) {
  const [filter, setFilter] = React.useState("");
  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const defaultColumn = useMemo(
    () => ({
      Filter: DefaultColumnFilter,
    }),
    [],
  );

  const table = useReactTable({
    data,
    columns,
    defaultColumn,
    state: {
      pagination,
      globalFilter: filter,
    },
    onPaginationChange: setPagination,
    onGlobalFilterChange: setFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // getRowCanExpand: () => true, // Enable expand functionality for all rows
    // getExpandedRowModel: getExpandedRowModel(),
  });

  if (loading) {
    return (
      <Loader>
        <SpinnerIcon size={60} />
      </Loader>
    );
  }

  return (
    <TableWrapper>
      <FilterInput
        type="text"
        placeholder="Search / Buscar..."
        value={filter}
        aria-label="Search"
        onChange={(e) => setFilter(e.target.value)}
      />
      {data.length === 0 && !loading ? (
        <NoDataMessage>No data found</NoDataMessage>
      ) : (
        <>
          <TableContainer>
            <TableElement>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHeaderCell
                        key={header.id}
                        // width={header.column.columnDef.size}
                        style={{
                          width: `${header.column.columnDef.size}px`,
                          minWidth: `${header.column.columnDef.size}px`,
                        }}
                        colSpan={header.colSpan}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        {{
                          asc: <FaCaretUp />,
                          desc: <FaCaretDown />,
                        }[header.column.getIsSorted()] ?? null}
                      </TableHeaderCell>
                    ))}
                  </tr>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <React.Fragment key={row.id}>
                    <TableRow
                    // key={row.id}
                    // onClick={() => handleRowClick(row)}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          // width={cell.column.columnDef.size}
                          style={{
                            width: `${cell.column.columnDef.size}px`,
                            minWidth: `${cell.column.columnDef.size}px`,
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>

                    {/* {row.getIsExpanded() && (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length}
                          style={{ backgroundColor: "#f9f9f9" }}
                        >
                          <div
                            dangerouslySetInnerHTML={{
                              __html: row.original.messageToRequestor_F,
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    )} */}
                  </React.Fragment>
                ))}
              </TableBody>
            </TableElement>
          </TableContainer>
          <PaginationWrapper>
            <PaginationInfo>
              Page {table.getState().pagination.pageIndex + 1} of{" "}
              {table.getPageCount()} | Total Records:{" "}
              {table.getPrePaginationRowModel().rows.length}
            </PaginationInfo>
            <Pagination>
              <PaginationButton
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
              >
                <MdFirstPage size={22} aria-label="first-page" />
              </PaginationButton>
              <PaginationButton
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                <GrPrevious aria-label="previous-page" />
              </PaginationButton>
              <PaginationButton
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                <GrNext aria-label="next-page" />
              </PaginationButton>
              <PaginationButton
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
              >
                <MdLastPage size={22} aria-label="last-page" />
              </PaginationButton>
            </Pagination>
          </PaginationWrapper>
        </>
      )}
    </TableWrapper>
  );
}

export default Table;
